import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { runtimeProviderConfigService } from '../../services/runtimeOperationalService';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import useRuntimeMutation from '../../hooks/useRuntimeMutation';
import useRuntimeActionLock from '../../hooks/useRuntimeActionLock';
import { RuntimeEmptyState, RuntimeErrorState, RuntimeLoadingState } from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

const SOURCE_TAG_COLOR = {
  tenant_override: 'blue',
  platform_global_billing: 'green',
  global_fallback: 'gold',
};

const normalizeJsonObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
};

const safeParseJsonObject = (rawText) => {
  const trimmed = String(rawText || '').trim();
  if (!trimmed) {
    return {};
  }

  const parsed = JSON.parse(trimmed);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Config deve ser um objeto JSON valido.');
  }
  return parsed;
};

const maskedCredentialTag = (maskedMap) => {
  const entries = Object.entries(normalizeJsonObject(maskedMap));
  const configured = entries.some(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');
  return configured ? <Tag color="green">Configurada (mascarada)</Tag> : <Tag color="default">Nao configurada</Tag>;
};

const sourceTag = (source) => {
  const normalized = String(source || '').toLowerCase();
  const color = SOURCE_TAG_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(source || '-').toUpperCase()}</Tag>;
};

const environmentTag = (config) => {
  const environment = String(normalizeJsonObject(config).environment || '').trim().toLowerCase();
  if (!environment) {
    return <Tag>-</Tag>;
  }

  if (environment === 'live' || environment === 'production' || environment === 'prod') {
    return <Tag color="red">LIVE</Tag>;
  }

  if (environment === 'sandbox' || environment === 'test' || environment === 'homolog') {
    return <Tag color="blue">SANDBOX</Tag>;
  }

  return <Tag color="default">{environment.toUpperCase()}</Tag>;
};

const formatJsonPreview = (value) => {
  try {
    return JSON.stringify(normalizeJsonObject(value), null, 2);
  } catch (error) {
    return '{}';
  }
};

const PlatformRuntimeProviderConfig = () => {
  const [tenantForm] = Form.useForm();
  const [globalForm] = Form.useForm();
  const [globalCredentialForm] = Form.useForm();
  const [tenantId, setTenantId] = React.useState('');

  const { withActionLock, isLocked } = useRuntimeActionLock();

  const catalogFilters = React.useMemo(() => ({ active_only: false }), []);

  const providersQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.providerCatalog(catalogFilters),
    queryFn: async () => {
      const response = await runtimeProviderConfigService.listProviders();
      return response.data;
    },
  });

  const globalConfigQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.providerGlobalConfig(),
    queryFn: async () => {
      const response = await runtimeProviderConfigService.getGlobalConfig();
      return response.data;
    },
  });

  const normalizedTenantId = String(tenantId || '').trim();

  const tenantConfigQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.providerTenantConfig(normalizedTenantId),
    enabled: Boolean(normalizedTenantId),
    queryFn: async () => {
      const response = await runtimeProviderConfigService.getTenantConfig(normalizedTenantId);
      return response.data;
    },
  });

  const providers = Array.isArray(providersQuery.data?.items) ? providersQuery.data.items : [];
  const globalConfig = globalConfigQuery.data || null;
  const tenantConfig = tenantConfigQuery.data || null;

  React.useEffect(() => {
    if (!globalConfig) {
      return;
    }

    globalForm.setFieldsValue({
      provider_key: globalConfig.provider_key,
      enabled: Boolean(globalConfig.enabled),
    });
    globalCredentialForm.setFieldsValue({
      provider_key: globalConfig.provider_key,
      api_key: '',
      asaas_api_key: '',
      access_token: '',
    });
  }, [globalConfig, globalForm, globalCredentialForm]);

  React.useEffect(() => {
    if (!tenantConfig) {
      tenantForm.setFieldsValue({
        provider_key: undefined,
        enabled: true,
        config_json: '{\n\n}',
      });
      return;
    }

    tenantForm.setFieldsValue({
      provider_key: tenantConfig.provider_key,
      enabled: Boolean(tenantConfig.enabled),
      config_json: formatJsonPreview(tenantConfig.config),
    });
  }, [tenantConfig, tenantForm]);

  const commonInvalidateKeys = React.useMemo(
    () => [
      runtimeQueryKeys.providerCatalog(catalogFilters),
      runtimeQueryKeys.providerGlobalConfig(),
      runtimeQueryKeys.providerTenantConfig(normalizedTenantId),
    ],
    [catalogFilters, normalizedTenantId]
  );

  const updateGlobalMutation = useRuntimeMutation({
    mutationFn: async (payload) => {
      const response = await runtimeProviderConfigService.updateGlobalDefault(payload);
      return response.data;
    },
    invalidateKeys: commonInvalidateKeys,
  });

  const updateGlobalConfigMutation = useRuntimeMutation({
    mutationFn: async (payload) => {
      const response = await runtimeProviderConfigService.updateGlobalConfig(payload);
      return response.data;
    },
    invalidateKeys: commonInvalidateKeys,
  });

  const updateTenantMutation = useRuntimeMutation({
    mutationFn: async ({ targetTenantId, payload }) => {
      const response = await runtimeProviderConfigService.updateTenantConfig(targetTenantId, payload);
      return response.data;
    },
    invalidateKeys: commonInvalidateKeys,
  });

  const deleteTenantMutation = useRuntimeMutation({
    mutationFn: async (targetTenantId) => {
      const response = await runtimeProviderConfigService.deleteTenantConfig(targetTenantId);
      return response.data;
    },
    invalidateKeys: commonInvalidateKeys,
  });

  const handleSearchTenant = React.useCallback(() => {
    const nextTenantId = String(tenantForm.getFieldValue('tenant_id') || '').trim();
    setTenantId(nextTenantId);
  }, [tenantForm]);

  const lockKeyUpdateGlobal = 'runtime-provider-config:update-global';
  const lockKeyUpdateGlobalCredentials = 'runtime-provider-config:update-global-credentials';
  const lockKeyUpdateTenant = `runtime-provider-config:update-tenant:${normalizedTenantId || '-'}`;
  const lockKeyDeleteTenant = `runtime-provider-config:delete-tenant:${normalizedTenantId || '-'}`;

  const onSubmitGlobal = async () => {
    const values = await globalForm.validateFields();

    Modal.confirm({
      title: 'Confirmar alteracao global/default',
      content: 'Essa alteracao impacta o provider default de billing da plataforma. Deseja continuar?',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        const run = await withActionLock(lockKeyUpdateGlobal, async () => {
          await updateGlobalMutation.mutateAsync({
            provider_key: String(values.provider_key || '').trim(),
            enabled: Boolean(values.enabled),
          });
        });

        if (run?.skipped) {
          return;
        }

        message.success('Config global/default atualizada com sucesso.');
      },
      onCancel: () => {},
    });
  };

  const onSubmitGlobalCredentials = async () => {
    const values = await globalCredentialForm.validateFields();
    const providerKey = String(values.provider_key || '').trim();
    if (!providerKey) {
      message.error('Selecione o provider para salvar credencial global da plataforma.');
      return;
    }

    const payloadCredentials = {};
    ['api_key', 'asaas_api_key', 'access_token'].forEach((field) => {
      const raw = values[field];
      if (raw === undefined || raw === null) {
        return;
      }
      const text = String(raw).trim();
      if (!text) {
        return;
      }
      payloadCredentials[field] = text;
    });

    if (Object.keys(payloadCredentials).length === 0) {
      message.error('Informe ao menos um campo de credencial (api_key, asaas_api_key ou access_token).');
      return;
    }

    Modal.confirm({
      title: 'Salvar credencial global da plataforma',
      content: 'A credencial sera usada no SaaS Billing da plataforma para cobrar tenants. Deseja continuar?',
      okText: 'Salvar credencial',
      cancelText: 'Cancelar',
      onOk: async () => {
        const run = await withActionLock(lockKeyUpdateGlobalCredentials, async () => {
          await updateGlobalConfigMutation.mutateAsync({
            provider_key: providerKey,
            enabled: Boolean(globalForm.getFieldValue('enabled')),
            config: normalizeJsonObject(globalConfig?.config),
            credentials: payloadCredentials,
          });
        });

        if (run?.skipped) {
          return;
        }

        globalCredentialForm.setFieldsValue({
          api_key: '',
          asaas_api_key: '',
          access_token: '',
        });
        message.success('Credencial global da plataforma salva com sucesso (armazenamento seguro).');
      },
      onCancel: () => {},
    });
  };

  const onSetProviderAsDefault = (providerRow) => {
    const providerKey = String(providerRow?.provider_key || '').trim();
    if (!providerKey) {
      message.error('Provider invalido para definir como padrao.');
      return;
    }

    Modal.confirm({
      title: 'Definir provider efetivo padrao',
      content: `Deseja definir ${providerRow.display_name || providerKey} como provider efetivo padrao global?`,
      okText: 'Definir como padrao',
      cancelText: 'Cancelar',
      onOk: async () => {
        const run = await withActionLock(lockKeyUpdateGlobal, async () => {
          await updateGlobalMutation.mutateAsync({
            provider_key: providerKey,
            enabled: true,
          });
          globalForm.setFieldsValue({
            provider_key: providerKey,
            enabled: true,
          });
        });

        if (run?.skipped) {
          return;
        }

        message.success('Provider efetivo padrao atualizado com sucesso.');
      },
      onCancel: () => {},
    });
  };

  const onSubmitTenantOverride = async () => {
    const values = await tenantForm.validateFields(['provider_key', 'enabled', 'config_json', 'tenant_id']);
    const targetTenantId = String(values.tenant_id || '').trim();
    if (!targetTenantId) {
      message.error('Informe tenant_id para salvar override.');
      return;
    }

    let parsedConfig;
    try {
      parsedConfig = safeParseJsonObject(values.config_json);
    } catch (error) {
      message.error(error.message || 'Config JSON invalida.');
      return;
    }

    Modal.confirm({
      title: 'Confirmar save de override por tenant',
      content: 'A configuracao de provider deste tenant sera atualizada. Deseja continuar?',
      okText: 'Salvar override',
      cancelText: 'Cancelar',
      onOk: async () => {
        const run = await withActionLock(lockKeyUpdateTenant, async () => {
          await updateTenantMutation.mutateAsync({
            targetTenantId,
            payload: {
              provider_key: String(values.provider_key || '').trim(),
              enabled: Boolean(values.enabled),
              config: parsedConfig,
            },
          });
          setTenantId(targetTenantId);
        });

        if (run?.skipped) {
          return;
        }

        message.success('Override por tenant salvo com sucesso.');
      },
      onCancel: () => {},
    });
  };

  const onDeleteTenantOverride = () => {
    const targetTenantId = String(tenantForm.getFieldValue('tenant_id') || '').trim();
    if (!targetTenantId) {
      message.error('Informe tenant_id para remover override.');
      return;
    }

    Modal.confirm({
      title: 'Confirmar remocao de override por tenant',
      content: 'Todos os overrides billing do tenant serao removidos e ele passara a usar o default/global.',
      okText: 'Remover override',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        const run = await withActionLock(lockKeyDeleteTenant, async () => {
          await deleteTenantMutation.mutateAsync(targetTenantId);
          await tenantConfigQuery.refetch();
        });

        if (run?.skipped) {
          return;
        }

        message.success('Override por tenant removido com sucesso.');
      },
      onCancel: () => {},
    });
  };

  const providersError = providersQuery.isError
    ? buildApiErrorMessage(providersQuery.error, 'Nao foi possivel carregar providers.')
    : null;
  const globalError = globalConfigQuery.isError
    ? buildApiErrorMessage(globalConfigQuery.error, 'Nao foi possivel carregar config global/default.')
    : null;
  const tenantError = tenantConfigQuery.isError
    ? buildApiErrorMessage(tenantConfigQuery.error, 'Nao foi possivel carregar config por tenant.')
    : null;

  const providersColumns = [
    {
      title: 'Provider',
      key: 'provider',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.display_name || row.provider_key}</Text>
          <Text type="secondary">{row.provider_key}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (value) => (value ? <Tag color="green">ENABLED</Tag> : <Tag color="red">DISABLED</Tag>),
    },
    {
      title: 'Config Base',
      dataIndex: 'has_default_config',
      key: 'has_default_config',
      render: (value) => (value ? <Tag color="blue">SIM</Tag> : <Tag>NAO</Tag>),
    },
    {
      title: 'Default Efetivo',
      dataIndex: 'is_global_default',
      key: 'is_global_default',
      render: (value) => (value ? <Tag color="green">SIM</Tag> : <Tag>NAO</Tag>),
    },
    {
      title: 'Capabilities',
      key: 'capabilities',
      render: (_, row) => {
        const capabilities = normalizeJsonObject(row.capabilities);
        const keys = Object.keys(capabilities);
        if (!keys.length) {
          return '-';
        }
        return (
          <Space wrap>
            {keys.map((item) => (
              <Tag key={`${row.provider_key}-${item}`}>{item}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => onSetProviderAsDefault(row)}
          disabled={Boolean(row.is_global_default) || isLocked(lockKeyUpdateGlobal)}
          loading={updateGlobalMutation.isPending && !row.is_global_default}
        >
          Definir como padrao
        </Button>
      ),
    },
  ];

  const tenantLockBusy =
    isLocked(lockKeyUpdateTenant) ||
    isLocked(lockKeyDeleteTenant) ||
    updateTenantMutation.isPending ||
    deleteTenantMutation.isPending;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Provider Configuration
          </Title>
          <Text type="secondary">Gestao read-safe de provider billing SaaS para Platform Admin.</Text>
        </div>

        <Alert
          type="warning"
          showIcon
          message="Impacto operacional"
          description="Alteracoes de provider/default e overrides podem impactar processamento de billing. Nunca execute retry/replay/charge a partir desta tela."
        />

        <Alert
          type="info"
          showIcon
          message="Semantica de configuracao"
          description="Config Base indica apenas que o provider possui configuracao catalogada. Default Efetivo indica quem sera usado pelo resolver global quando nao houver override de tenant."
        />

        <Card title="Config Global / Default Provider">
          {globalConfigQuery.isLoading ? <RuntimeLoadingState tip="Carregando config global/default..." /> : null}
          {!globalConfigQuery.isLoading && globalConfigQuery.isError ? (
            <RuntimeErrorState
              title="Erro ao carregar config global/default"
              description={globalError?.message || 'Falha inesperada.'}
              onRetry={() => globalConfigQuery.refetch()}
            />
          ) : null}

          {!globalConfigQuery.isLoading && !globalConfigQuery.isError && !globalConfig ? (
            <RuntimeEmptyState
              title="Config global/default indisponivel"
              description="Nao foi possivel obter a configuracao efetiva global."
            />
          ) : null}

          {!globalConfigQuery.isLoading && !globalConfigQuery.isError && globalConfig ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Provider efetivo">{globalConfig.provider_display_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Provider key">{globalConfig.provider_key || '-'}</Descriptions.Item>
                <Descriptions.Item label="Source">{sourceTag(globalConfig.source)}</Descriptions.Item>
                <Descriptions.Item label="Enabled">
                  {globalConfig.enabled ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Modo">{environmentTag(globalConfig.config)}</Descriptions.Item>
                <Descriptions.Item label="Credencial">{maskedCredentialTag(globalConfig.credentials_masked)}</Descriptions.Item>
              </Descriptions>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={16}>
                  <Form form={globalForm} layout="vertical" onFinish={onSubmitGlobal}>
                    <Form.Item
                      name="provider_key"
                      label="Provider default"
                      rules={[{ required: true, message: 'Selecione um provider.' }]}
                    >
                      <Select
                        placeholder="Selecione provider default"
                        options={providers.map((provider) => ({
                          value: provider.provider_key,
                          label: `${provider.display_name} (${provider.provider_key})`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item name="enabled" label="Enabled" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={updateGlobalMutation.isPending}
                      disabled={isLocked(lockKeyUpdateGlobal)}
                    >
                      Salvar Global Default
                    </Button>
                  </Form>
                </Col>
                <Col xs={24} md={8}>
                  <Text type="secondary">Config publica efetiva:</Text>
                  <pre
                    style={{
                      marginTop: 8,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 260,
                      overflow: 'auto',
                      background: '#fafafa',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 12,
                    }}
                  >
                    {formatJsonPreview(globalConfig.config)}
                  </pre>
                </Col>
              </Row>

              <Card type="inner" title="Credencial Global da Plataforma (SaaS Billing)">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Separacao de contexto"
                  description="Esta credencial e da plataforma FisiSaaS para cobrar tenants. Nao utiliza ASAAS operacional do tenant/ERP."
                />
                <Form form={globalCredentialForm} layout="vertical" onFinish={onSubmitGlobalCredentials}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="provider_key"
                        label="Provider"
                        rules={[{ required: true, message: 'Selecione provider.' }]}
                      >
                        <Select
                          placeholder="Provider para credencial global"
                          options={providers.map((provider) => ({
                            value: provider.provider_key,
                            label: `${provider.display_name} (${provider.provider_key})`,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="api_key" label="api_key (write-only)">
                        <Input.Password autoComplete="new-password" placeholder="sk_..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="asaas_api_key" label="asaas_api_key (write-only)">
                        <Input.Password autoComplete="new-password" placeholder="$aact_..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="access_token" label="access_token (write-only)">
                        <Input.Password autoComplete="new-password" placeholder="token" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateGlobalConfigMutation.isPending}
                    disabled={isLocked(lockKeyUpdateGlobalCredentials)}
                  >
                    Salvar Credencial Global
                  </Button>
                </Form>
              </Card>
            </Space>
          ) : null}
        </Card>

        <Card title="Providers Disponiveis">
          {providersQuery.isLoading ? <RuntimeLoadingState tip="Carregando providers..." /> : null}
          {!providersQuery.isLoading && providersQuery.isError ? (
            <RuntimeErrorState
              title="Erro ao carregar providers"
              description={providersError?.message || 'Falha inesperada.'}
              onRetry={() => providersQuery.refetch()}
            />
          ) : null}
          {!providersQuery.isLoading && !providersQuery.isError && providers.length === 0 ? (
            <RuntimeEmptyState title="Sem providers" description="Nenhum provider billing foi encontrado." />
          ) : null}
          {!providersQuery.isLoading && !providersQuery.isError && providers.length > 0 ? (
            <Table
              rowKey={(record) => String(record.id || record.provider_key)}
              columns={providersColumns}
              dataSource={providers}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
            />
          ) : null}
        </Card>

        <Card title="Override por Tenant">
          <Form
            form={tenantForm}
            layout="vertical"
            initialValues={{ tenant_id: '', enabled: true, config_json: '{\n\n}' }}
            onFinish={onSubmitTenantOverride}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="tenant_id"
                  label="Tenant ID"
                  rules={[{ required: true, message: 'Informe tenant_id.' }]}
                >
                  <Input placeholder="tenant_id" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="provider_key"
                  label="Provider"
                  rules={[{ required: true, message: 'Selecione um provider.' }]}
                >
                  <Select
                    placeholder="Provider para override"
                    options={providers.map((provider) => ({
                      value: provider.provider_key,
                      label: `${provider.display_name} (${provider.provider_key})`,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="enabled" label="Enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="config_json"
              label="Config JSON (sem segredos)"
              tooltip="Nao incluir segredos aqui; este campo e para configuracao publica."
              rules={[{ required: true, message: 'Informe config JSON.' }]}
            >
              <Input.TextArea rows={8} spellCheck={false} />
            </Form.Item>

            <Space wrap>
              <Button onClick={handleSearchTenant} loading={tenantConfigQuery.isFetching}>
                Carregar tenant
              </Button>
              <Button type="primary" htmlType="submit" loading={updateTenantMutation.isPending} disabled={tenantLockBusy}>
                Salvar Override Tenant
              </Button>
              <Button danger onClick={onDeleteTenantOverride} loading={deleteTenantMutation.isPending} disabled={tenantLockBusy}>
                Remover Override Tenant
              </Button>
            </Space>
          </Form>

          <div style={{ marginTop: 16 }}>
            {!normalizedTenantId ? (
              <RuntimeEmptyState
                title="Nenhum tenant selecionado"
                description="Informe tenant_id e clique em carregar tenant para ver config efetiva."
              />
            ) : null}

            {normalizedTenantId && tenantConfigQuery.isLoading ? (
              <RuntimeLoadingState tip="Carregando config do tenant..." />
            ) : null}

            {normalizedTenantId && !tenantConfigQuery.isLoading && tenantConfigQuery.isError ? (
              <RuntimeErrorState
                title="Erro ao carregar config do tenant"
                description={tenantError?.message || 'Falha inesperada.'}
                onRetry={() => tenantConfigQuery.refetch()}
              />
            ) : null}

            {normalizedTenantId && !tenantConfigQuery.isLoading && !tenantConfigQuery.isError && tenantConfig ? (
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Tenant ID">{tenantConfig.tenant_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="Source">{sourceTag(tenantConfig.source)}</Descriptions.Item>
                <Descriptions.Item label="Provider">{tenantConfig.provider_display_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Provider key">{tenantConfig.provider_key || '-'}</Descriptions.Item>
                <Descriptions.Item label="Enabled">
                  {tenantConfig.enabled ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Modo">{environmentTag(tenantConfig.config)}</Descriptions.Item>
                <Descriptions.Item label="Configured">
                  {tenantConfig.configured ? <Tag color="green">SIM</Tag> : <Tag color="orange">NAO</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Credencial">{maskedCredentialTag(tenantConfig.credentials_masked)}</Descriptions.Item>
              </Descriptions>
            ) : null}
          </div>
        </Card>
      </Space>
    </div>
  );
};

export default PlatformRuntimeProviderConfig;
